
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CommunityMember } from "@/types";

interface EditStaffDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  staffMember: CommunityMember;
}

export function EditStaffDialog({ open, onOpenChange, staffMember }: EditStaffDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: staffMember.name,
    category: staffMember.category,
    residence: staffMember.residence || "",
    contact: staffMember.contact || "",
  });

  useEffect(() => {
    // Update form data if staffMember changes
    setFormData({
      name: staffMember.name,
      category: staffMember.category,
      residence: staffMember.residence || "",
      contact: staffMember.contact || "",
    });
  }, [staffMember]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from("community_members")
        .update({
          name: formData.name,
          category: formData.category,
          residence: formData.residence || null,
          contact: formData.contact || null,
        })
        .eq("id", staffMember.id);

      if (error) {
        console.error('Error updating staff member:', error);
        throw error;
      }

      toast({
        title: "Success",
        description: "Staff member updated successfully",
      });
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error updating staff member:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update staff member",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Staff Member</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Category</Label>
            <RadioGroup
              value={formData.category}
              onValueChange={(value: "TEACHING_STAFF" | "NON_TEACHING_STAFF" | "ADMIN") => 
                setFormData(prev => ({ ...prev, category: value }))
              }
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="TEACHING_STAFF" id="teaching" />
                <Label htmlFor="teaching">Teaching Staff</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="NON_TEACHING_STAFF" id="non-teaching" />
                <Label htmlFor="non-teaching">Non Teaching Staff</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="ADMIN" id="admin" />
                <Label htmlFor="admin">Admin</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="residence">Residence (Optional)</Label>
            <Input
              id="residence"
              value={formData.residence}
              onChange={(e) => setFormData(prev => ({ ...prev, residence: e.target.value }))}
              placeholder="Staff residence location"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact">Contact (Optional)</Label>
            <Input
              id="contact"
              value={formData.contact}
              onChange={(e) => setFormData(prev => ({ ...prev, contact: e.target.value }))}
              placeholder="Phone number"
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Updating..." : "Update Staff Member"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
